import { useEffect, useState } from "react";
import styles from "./TariffsSection.module.scss";
import Countries from "../../../shared/countries/Countries";
import { TariffsService } from "../../../../services/tariffs.service";
import { TariffsDataType } from "../../../../interface/site.interface";
import Spinner from "../../../shared/spinner/Spinner";
import { useSite } from "../../../../context/TranslationContext";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { useNavigate, useParams } from "react-router-dom";
import { useLang } from "../../../../hooks/useLangPath";
import { ROUTE_SLUGS } from "../../../../constants/route-slugs";
import TitleUpdater from "../../../shared/title/TitleUpdater";

const TariffsSection = ({ enableUrlSync = false }: { enableUrlSync?: boolean }) => {
  const [tariffs, setTariffs] = useState<TariffsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [productsDetail, setProductsDetail] = useState<any[]>([]);
  const [activeTariff, setActiveTariff] = useState<TariffsDataType | null>(null);
  const language = useSelector((state: RootState) => state.language.language);
  const { data } = useSelector((state: RootState) => state.translationData);
  const localizeWeight = (text: string) =>
    data?.weight_unit ? text.replace(/kq/gi, data.weight_unit) : text;
  const backgrounds = ["#F8F4EE", "#DFECE2"];
  const navigate = useNavigate();
  const lang = useLang();
  const { countrySlug } = useParams<{ countrySlug?: string }>();

  const selectCountry = (tariff: TariffsDataType) => {
    setActiveTariff(tariff);
    setProductsDetail(tariff.products || []);
    if (enableUrlSync) {
      const slug = tariff.slug;
      navigate(
        slug
          ? `/${lang}/${ROUTE_SLUGS.tariffs[lang]}/${slug}`
          : `/${lang}/${ROUTE_SLUGS.tariffs[lang]}`,
        { replace: true }
      );
    }
  };

  const handleCountryChange = (countryName: string) => {
    const selected = tariffs.find((t) => t.country === countryName);
    if (selected) selectCountry(selected);
  };

  useEffect(() => {
    setLoading(true);
    TariffsService.getAllTariffs()
      .then((res) => {
        const fetchedTariffs: TariffsDataType[] = res.data.original;
        setTariffs(fetchedTariffs);
        if (fetchedTariffs.length === 0) return;

        const target = countrySlug
          ? fetchedTariffs.find((t) => t.slug === countrySlug) ?? fetchedTariffs[0]
          : fetchedTariffs[0];

        setActiveTariff(target);
        setProductsDetail(target.products || []);

        // Only update URL on tariffs page
        if (enableUrlSync && target.slug && !countrySlug) {
          navigate(
            `/${lang}/${ROUTE_SLUGS.tariffs[lang]}/${target.slug}`,
            { replace: true }
          );
        }
      })
      .catch((err) => {
        console.error("Error fetching tariffs:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [language]);

  return (
    <div id={styles.tariffs_sections}>
      {enableUrlSync && (
        <TitleUpdater
          title={activeTariff?.title || `${data?.colibri} | ${data?.tariffs}` || "Colibri"}
          description={activeTariff?.description ?? null}
          keywords={activeTariff?.keywords ?? null}
        />
      )}
      <div className="container">
        <div className={styles.tariffs_sections}>
          <h1 className={styles.section_title}>{data?.tariffs}</h1>
          <div className={styles.countries_wrapper}>
            <div className={styles.name_buttons}>
              <Countries onCountrySelect={handleCountryChange} />
            </div>
            <div className={styles.products_infos}>
              {loading ? (
                <Spinner />
              ) : (
                <div className={styles.detail_sections}>
                  {productsDetail.length === 0 ? (
                    <div className={styles.no_product}>
                      <p>Bu ölkə üçün tariflər yoxdur</p>
                    </div>
                  ) : (
                    productsDetail?.map((element: any, index: number) => (
                      <div
                        key={index}
                        className={styles.detail_card}
                        style={{
                          background: backgrounds[index % backgrounds.length],
                        }}
                      >
                        <div className={styles.card_head}>
                          <div className={styles.type}>
                            <h1>{element.type}</h1>
                          </div>
                        </div>
                        <ul className={styles.pricing_detail}>
                          {element.pricing.map((el: any) => (
                            <li
                              key={el.weightRange}
                              className={styles.detail_list}
                            >
                              <div>{localizeWeight(el.weightRange)}</div>
                              <div>
                                {el.priceAZN} AZN | {el.price} {el.currency}
                              </div>
                            </li>
                          ))}

                          {element.additionalPricePerKg.map(
                            (add_price: any, idx: number) => (
                              <li
                                key={idx}
                                className={`${styles.detail_list} ${styles.last_detail}`}
                              >
                                <div
                                  className={styles.additional_price}
                                  key={idx}
                                >
                                  <div>{localizeWeight(add_price.weightRange)}</div>
                                  <div>
                                    <p className={styles.perkg}>
                                      {data?.per_kg}{" "}
                                    </p>
                                    {add_price.priceAZN} AZN | {add_price.price}{" "}
                                    {add_price.currency}
                                  </div>
                                </div>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TariffsSection;
